import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F0E8]" data-testid="privacy-policy-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#2A2A2A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B5703A]/20 rounded-full mb-6">
              <Shield className="w-8 h-8 text-[#B5703A]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-[#F5F0E8] mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#C4B6A4] text-lg">
              Your privacy matters to us
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Back Link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#B5703A] hover:text-[#8B5A2B] transition-colors mb-12"
              data-testid="back-to-home"
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-medium">Back to Home</span>
            </Link>

            {/* Privacy Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-[#3D3D3D] text-lg leading-relaxed mb-8">
                At Living Craftsmen Interiors, we value your privacy and are committed to protecting your personal information.
              </p>

              {/* Information We Collect */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  Information We Collect
                </h2>
                <p className="text-[#3D3D3D] mb-4">
                  We may collect the following information through our website, forms, or advertisements:
                </p>
                <ul className="space-y-2 text-[#3D3D3D]">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Name</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Phone number</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Email address</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Property details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Project requirements</span>
                  </li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  How We Use Your Information
                </h2>
                <p className="text-[#3D3D3D] mb-4">
                  The information collected may be used to:
                </p>
                <ul className="space-y-2 text-[#3D3D3D]">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Contact you regarding interior design services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Share quotations and project details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Schedule consultations or site visits</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#B5703A] rounded-full mt-2.5 flex-shrink-0"></span>
                    <span>Improve our services and customer experience</span>
                  </li>
                </ul>
              </div>

              {/* Information Sharing */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  Information Sharing
                </h2>
                <p className="text-[#3D3D3D]">
                  We do not sell, rent, or share your personal information with third parties except where required for project execution or by law.
                </p>
              </div>

              {/* Data Security */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  Data Security
                </h2>
                <p className="text-[#3D3D3D]">
                  We take reasonable measures to protect your information from unauthorized access or misuse.
                </p>
              </div>

              {/* Cookies */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  Cookies
                </h2>
                <p className="text-[#3D3D3D]">
                  Our website may use cookies to improve browsing experience and analyze website traffic.
                </p>
              </div>

              {/* Contact Us */}
              <div className="mb-10">
                <h2 className="text-2xl font-serif text-[#2A2A2A] mb-4">
                  Contact Us
                </h2>
                <div className="bg-[#2A2A2A] p-6 rounded-lg">
                  <p className="text-[#F5F0E8] font-serif text-xl mb-4">Living Craftsmen Interiors</p>
                  <div className="space-y-2">
                    <p className="text-[#C4B6A4]">
                      Website:{' '}
                      <a 
                        href="https://www.livingcraftsmen.com" 
                        className="text-[#B5703A] hover:underline"
                      >
                        www.livingcraftsmen.com
                      </a>
                    </p>
                    <p className="text-[#C4B6A4]">
                      Email:{' '}
                      <a 
                        href="mailto:connect@livingcraftsmen.com" 
                        className="text-[#B5703A] hover:underline"
                      >
                        connect@livingcraftsmen.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Consent Notice */}
              <div className="border-t border-[#C4B6A4] pt-8 mt-12">
                <p className="text-[#3D3D3D] text-center italic">
                  By using our website, you consent to this Privacy Policy.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
